import { Badge, Group, Stack, Text, Tooltip } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { AlertMarker } from "@/wallet/migrations";
import type { ForwardKeyState } from "@/wallet/residuals";
import { forwardOutcome, SKIP_REASON_LABELS, TRIGGER_LABELS } from "@/wallet/residuals";
import type { ResidualForwardDoc } from "@/firestore/types";
import { Timestamp } from "./Timestamp";

/** Chips for the backend alert markers the console can see; each tooltip says what to do. */
export function AlertMarkers({ markers }: { markers: readonly AlertMarker[] }) {
  if (markers.length === 0) return null;
  return (
    <Group gap={4} data-testid="alert-markers">
      {markers.map((m) => (
        <Tooltip
          key={`${m.kind}-${m.label}`}
          label={`${m.kind}: ${m.detail}`}
          multiline
          w={360}
          withArrow
        >
          <Badge
            color={m.color}
            variant={m.color === "red" ? "filled" : "light"}
            radius="sm"
            tt="none"
            leftSection={m.color === "red" ? <IconAlertTriangle size={12} /> : undefined}
          >
            {m.label}
          </Badge>
        </Tooltip>
      ))}
    </Group>
  );
}

/** A forward key's state (signed / pending / expired / revoked / missing) with its expiry. */
export function ForwardKeyBadge({
  state,
  withExpiry = false,
}: {
  state: ForwardKeyState;
  withExpiry?: boolean;
}) {
  return (
    <Stack gap={0}>
      <Tooltip label={state.note} multiline w={340} withArrow>
        <Badge
          color={state.color}
          variant={state.live ? "filled" : "light"}
          radius="sm"
          tt="none"
          data-testid={`forward-key-${state.role}`}
        >
          {state.label}
          {state.live ? " · live" : ""}
        </Badge>
      </Tooltip>
      {withExpiry && state.expiresAt ? (
        <Text size="xs" c="dimmed">
          {state.status === "expired" ? "expired" : "until"} <Timestamp value={state.expiresAt} />
        </Text>
      ) : null}
    </Stack>
  );
}

/** A forward's outcome as the owner's app reads it, with its trigger in the tooltip. */
export function ForwardOutcomeBadge({
  forward,
}: {
  forward: Pick<ResidualForwardDoc, "state" | "skipReason" | "trigger">;
}) {
  const outcome = forwardOutcome(forward);
  const reason = forward.skipReason
    ? (SKIP_REASON_LABELS[forward.skipReason] ?? forward.skipReason)
    : null;
  return (
    <Tooltip
      label={`${forward.state}${reason ? ` (${forward.skipReason})` : ""} · trigger: ${TRIGGER_LABELS[forward.trigger] ?? forward.trigger}`}
      multiline
      w={300}
      withArrow
    >
      <Badge color={outcome.color} variant="light" radius="sm" tt="none">
        {outcome.label}
      </Badge>
    </Tooltip>
  );
}
